import AppHeader from "@/components/AppHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ArrowRight } from "@/lib/icons";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

type Lesson = {
  id: string;
  title: string;
  grade: string;
  duration: string;
  topic: string;
  objectives: string[];
  activities: string[];
  toolLink?: { label: string; path: string };
};

const lessons: Lesson[] = [
  {
    id: "atomic-structure",
    title: "Atomic Structure & the Periodic Table",
    grade: "9–10",
    duration: "60 min",
    topic: "Atomic Theory",
    objectives: [
      "Identify protons, neutrons, and electrons in any atom.",
      "Use the periodic table to determine atomic number and mass.",
      "Describe the arrangement of electrons in shells.",
    ],
    activities: [
      "Warm-up: Label parts of an atom diagram (5 min).",
      "Guided exploration with the interactive Periodic Table & 3D Atom (25 min).",
      "Pair practice: build electron configurations for elements 1–20 (20 min).",
      "Exit ticket: 3 questions on subatomic particles (10 min).",
    ],
    toolLink: { label: "Open 3D Atom", path: "/atom" },
  },
  {
    id: "ions-bonding",
    title: "Ions and Ionic Bonding",
    grade: "9–10",
    duration: "55 min",
    topic: "Bonding",
    objectives: [
      "Predict the charge of common monatomic ions.",
      "Write correct formulas for ionic compounds.",
      "Explain how ionic bonds form via electron transfer.",
    ],
    activities: [
      "Demo: form NaCl using the Ion Engine (10 min).",
      "Guided notes: cations vs. anions, polyatomic ions (15 min).",
      "Practice set: 15 ionic formulas, peer-checked (20 min).",
      "Wrap-up discussion + quick quiz (10 min).",
    ],
    toolLink: { label: "Open Ion Engine", path: "/ions" },
  },
  {
    id: "balancing",
    title: "Balancing Chemical Equations",
    grade: "10",
    duration: "50 min",
    topic: "Stoichiometry",
    objectives: [
      "Apply the law of conservation of mass to chemical equations.",
      "Balance equations using coefficients (not subscripts).",
      "Identify reaction type: synthesis, decomposition, single/double replacement, combustion.",
    ],
    activities: [
      "Mini-lecture with examples on the board (10 min).",
      "Equation Balancer tool - students balance 8 equations (20 min).",
      "Whiteboard challenge: trickiest combustion reactions (15 min).",
      "Exit ticket (5 min).",
    ],
    toolLink: { label: "Open Equation Balancer", path: "/tools/equation-balancer" },
  },
  {
    id: "molar-mass",
    title: "Moles and Molar Mass",
    grade: "10–11",
    duration: "60 min",
    topic: "Stoichiometry",
    objectives: [
      "Calculate the molar mass of any compound.",
      "Convert between grams, moles, and number of particles.",
      "Use Avogadro's number in real-world contexts.",
    ],
    activities: [
      "Demo: weigh a mole of various substances (10 min).",
      "Worked examples on conversions (15 min).",
      "Lab partners use the Molar Mass Calculator on 10 compounds (20 min).",
      "Independent practice + exit ticket (15 min).",
    ],
    toolLink: { label: "Open Molar Mass Calculator", path: "/tools/molar-mass" },
  },
  {
    id: "solubility",
    title: "Solubility Rules & Precipitation Reactions",
    grade: "10–11",
    duration: "55 min",
    topic: "Solutions",
    objectives: [
      "Apply standard solubility rules to predict precipitates.",
      "Write balanced molecular and net ionic equations.",
      "Identify spectator ions in aqueous reactions.",
    ],
    activities: [
      "Review solubility table (10 min).",
      "Virtual lab: mix solutions in the Online Lab (20 min).",
      "Practice writing net ionic equations (20 min).",
      "Wrap-up discussion (5 min).",
    ],
    toolLink: { label: "Open Solubility Table", path: "/tools/solubility" },
  },
  {
    id: "flame-tests",
    title: "Flame Tests & Emission Spectra",
    grade: "11–12",
    duration: "60 min",
    topic: "Atomic Spectra",
    objectives: [
      "Connect electron transitions to emitted wavelengths of light.",
      "Identify metal ions by characteristic flame colors.",
      "Interpret emission spectra to identify elements.",
    ],
    activities: [
      "Virtual flame tests for Na, K, Ca, Cu, Li in the Online Lab (20 min).",
      "Examine emission spectra in the 3D Atom view (15 min).",
      "Worksheet: match spectra to elements (20 min).",
      "Exit reflection (5 min).",
    ],
    toolLink: { label: "Open Online Lab", path: "/lab" },
  },
  {
    id: "organic-intro",
    title: "Introduction to Organic Chemistry",
    grade: "11–12",
    duration: "60 min",
    topic: "Organic",
    objectives: [
      "Recognize alkanes, alkenes, alkynes, and alcohols.",
      "Apply IUPAC naming rules to simple compounds.",
      "Describe properties trends within homologous series.",
    ],
    activities: [
      "Concept intro: functional groups gallery walk (15 min).",
      "Naming practice with IUPAC Naming tab (20 min).",
      "Group challenge: name 10 structures (20 min).",
      "Closing discussion (5 min).",
    ],
    toolLink: { label: "Open Organic Chemistry", path: "/organic" },
  },
  {
    id: "ph-acids",
    title: "Acids, Bases, and pH",
    grade: "10–11",
    duration: "55 min",
    topic: "Acids & Bases",
    objectives: [
      "Define acids and bases (Arrhenius & Brønsted–Lowry).",
      "Use the pH scale to classify solutions.",
      "Predict products of neutralization reactions.",
    ],
    activities: [
      "Hook: pH of household items demo (10 min).",
      "pH Test in the Online Lab with various solutions (20 min).",
      "Practice: write neutralization equations (20 min).",
      "Exit ticket (5 min).",
    ],
    toolLink: { label: "Open Online Lab", path: "/lab" },
  },
];

export default function LessonPlans() {
  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Chemistry Lesson Plans — ElementPad" description="Free chemistry lesson plans for Grades 9-12 aligned to common standards. Printable, customizable, ready to teach." path="/lessons" />
      <AppHeader />
      <Main className="py-6">
        <div className="max-w-[1200px] mx-auto px-4">
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-1">Lesson Plans</h1>
            <p className="text-sm text-muted-foreground">
              Ready-to-use chemistry lessons for grades 9–12, each linked to ElementPad's interactive tools.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {lessons.map((l) => (
              <Card key={l.id} className="flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle className="text-base leading-tight">{l.title}</CardTitle>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge variant="secondary" className="text-xs">Grade {l.grade}</Badge>
                      <span className="text-xs text-muted-foreground">{l.duration}</span>
                    </div>
                  </div>
                  <CardDescription className="text-xs">{l.topic}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col gap-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Objectives</p>
                    <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                      {l.objectives.map((o, i) => (
                        <li key={i}>{o}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Activities</p>
                    <ol className="space-y-1 text-muted-foreground list-decimal list-inside">
                      {l.activities.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ol>
                  </div>
                  {l.toolLink && (
                    <div className="mt-auto pt-2">
                      <Link
                        to={l.toolLink.path}
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium"
                      >
                        {l.toolLink.label}
                        <ArrowRight size={14} aria-hidden="true" />
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </Main>
    </div>
  );
}
